import { Button } from "../UIElements/Button";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "../UIElements/Icon";
import { NativeSelect } from "../UIElements/Select";
import { toast } from "sonner";
import {
  useEmailConfig,
  useSaveEmailConfig,
  SMTP_CONFIG_KEYS,
} from "../../../hooks/useEmailConfig";

const EmailConfigurationForm = ({ onConfigured, onCancel }) => {
  const backLabel = "Go Back";
  const navigate = useNavigate();
  const { data, isLoading } = useEmailConfig();
  const saveConfigMutation = useSaveEmailConfig();

  const [formData, setFormData] = useState({
    smtp_host: "",
    smtp_username: "",
    smtp_password: "",
    smtp_encryption: "TLS",
    smtp_port: "465",
    sender_email: "",
  });

  useEffect(() => {
    if (data?.configs) {
      setFormData({
        smtp_host: data.configs[SMTP_CONFIG_KEYS.HOST]?.value || "",
        smtp_username: data.configs[SMTP_CONFIG_KEYS.USERNAME]?.value || "",
        smtp_password: data.configs[SMTP_CONFIG_KEYS.PASSWORD]?.value || "",
        smtp_encryption:
          data.configs[SMTP_CONFIG_KEYS.ENCRYPTION]?.value || "TLS",
        smtp_port: data.configs[SMTP_CONFIG_KEYS.PORT]?.value || "465",
        sender_email: data.configs[SMTP_CONFIG_KEYS.SENDER_EMAIL]?.value || "",
      });
    }
  }, [data]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.smtp_host.trim()) {
      toast.error("SMTP Host is required");
      return;
    }
    if (!formData.smtp_username.trim()) {
      toast.error("SMTP Username is required");
      return;
    }
    if (!formData.smtp_password.trim()) {
      toast.error("SMTP Password is required");
      return;
    }
    if (!formData.sender_email.trim()) {
      toast.error("Sender Email Address is required");
      return;
    }

    try {
      const configs = data?.configs;
      if (!configs) {
        toast.error("Configuration data not loaded");
        return;
      }

      const smtpFieldUpdates = [
        {
          configId: configs[SMTP_CONFIG_KEYS.HOST]?.id,
          key: SMTP_CONFIG_KEYS.HOST,
          value: formData.smtp_host,
          originalValue: configs[SMTP_CONFIG_KEYS.HOST]?.value,
        },
        {
          configId: configs[SMTP_CONFIG_KEYS.USERNAME]?.id,
          key: SMTP_CONFIG_KEYS.USERNAME,
          value: formData.smtp_username,
          originalValue: configs[SMTP_CONFIG_KEYS.USERNAME]?.value,
        },
        {
          configId: configs[SMTP_CONFIG_KEYS.PASSWORD]?.id,
          key: SMTP_CONFIG_KEYS.PASSWORD,
          value: formData.smtp_password,
          originalValue: configs[SMTP_CONFIG_KEYS.PASSWORD]?.value,
        },
        {
          configId: configs[SMTP_CONFIG_KEYS.ENCRYPTION]?.id,
          key: SMTP_CONFIG_KEYS.ENCRYPTION,
          value: formData.smtp_encryption,
          originalValue: configs[SMTP_CONFIG_KEYS.ENCRYPTION]?.value,
        },
        {
          configId: configs[SMTP_CONFIG_KEYS.PORT]?.id,
          key: SMTP_CONFIG_KEYS.PORT,
          value: formData.smtp_port,
          originalValue: configs[SMTP_CONFIG_KEYS.PORT]?.value,
        },
        {
          configId: configs[SMTP_CONFIG_KEYS.SENDER_EMAIL]?.id,
          key: SMTP_CONFIG_KEYS.SENDER_EMAIL,
          value: formData.sender_email,
          originalValue: configs[SMTP_CONFIG_KEYS.SENDER_EMAIL]?.value,
        },
      ];

      const updates = smtpFieldUpdates
        .filter((u) => u.configId && u.value !== (u.originalValue || ""))
        .map(({ configId, key, value }) => ({ configId, key, value }));

      const useDefaultConfig = configs[SMTP_CONFIG_KEYS.USE_DEFAULT];
      if (useDefaultConfig?.id && useDefaultConfig?.value === "1") {
        updates.push({
          configId: useDefaultConfig.id,
          key: SMTP_CONFIG_KEYS.USE_DEFAULT,
          value: "0",
        });
      }

      if (updates.length === 0) {
        toast.info("No changes to save");
        onConfigured?.();
        return;
      }

      await saveConfigMutation.mutateAsync(updates);
      toast.success("Email configuration saved successfully");
      onConfigured?.();
    } catch (error) {
      toast.error("Failed to save configuration", {
        description: error.message,
      });
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/campaigns");
    }
  };

  if (isLoading) {
    return (
      <div
        className="ordering-campaigns-surface flex items-center justify-center py-20"
        role="status"
        aria-busy="true"
      >
        <div className="border-primary h-8 w-8 animate-spin rounded-full border-b-2"></div>
      </div>
    );
  }

  const hasSmtpConfigs =
    data?.raw?.length > 0 && data?.configs?.[SMTP_CONFIG_KEYS.HOST];

  if (!hasSmtpConfigs) {
    return (
      <div
        className="ordering-campaigns-surface mx-auto max-w-2xl"
        data-ordering-expression="product"
      >
        <div className="mb-6 flex items-center gap-4">
          {onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              aria-label={backLabel}
              className="text-muted-foreground hover:bg-card hover:text-foreground rounded-full p-2 transition-colors"
            >
              <Icon name="ArrowLeft" className="h-5 w-5" />
            </button>
          )}
          <div>
            <h2 className="text-foreground text-xl font-bold">
              Email Server Configuration
            </h2>
            <p className="text-muted-foreground mt-1 text-sm">
              Configure your SMTP settings for outbound marketing emails.
            </p>
          </div>
        </div>

        <div className="border-border bg-background rounded-xl border p-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[var(--warning-bg)]">
            <Icon name="AlertTriangle" className="text-warning h-8 w-8" />
          </div>
          <h3 className="text-foreground mb-2 text-lg font-semibold">
            Email Configuration Not Available
          </h3>
          <p className="text-muted-foreground mx-auto mb-6 max-w-md text-sm">
            SMTP email settings are not available for your current plan. Please
            contact support to enable email marketing features for your account.
          </p>
          <div className="flex items-center justify-center gap-3">
            {onCancel && (
              <button
                type="button"
                onClick={handleCancel}
                className="text-muted-foreground hover:text-foreground px-6 py-2 transition-colors"
              >
                Go Back
              </button>
            )}
            <Button
              variant="default"
              type="button"
              onClick={() => navigate("/support")}
              className="flex items-center gap-2 px-6 py-2"
            >
              <Icon name="MessageCircle" className="h-4 w-4" />
              Contact Support
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const useDefault =
    data?.configs?.[SMTP_CONFIG_KEYS.USE_DEFAULT]?.value === "1";

  return (
    <div
      className="ordering-campaigns-surface mx-auto max-w-2xl"
      data-ordering-expression="product"
    >
      {/* Header */}
      <div className="mb-6 flex items-center gap-4">
        {onCancel && (
          <button
            type="button"
            onClick={handleCancel}
            aria-label={backLabel}
            className="text-muted-foreground hover:bg-card hover:text-foreground rounded-full p-2 transition-colors"
          >
            <Icon name="ArrowLeft" className="h-5 w-5" />
          </button>
        )}
        <div>
          <h2 className="text-foreground text-xl font-bold">
            Email Server Configuration
          </h2>
          <p className="text-muted-foreground mt-1 text-sm">
            Configure your SMTP settings for outbound marketing emails.
          </p>
        </div>
      </div>

      {/* Default SMTP Info Banner */}
      {useDefault && (
        <div className="border-primary/30 bg-accent/10 mb-6 flex items-start gap-2 rounded-lg border p-3">
          <Icon
            name="Info"
            className="text-primary mt-0.5 h-4 w-4 flex-shrink-0"
          />
          <p className="text-primary text-sm">
            You are currently using Ordering.co default SMTP server. Your email
            campaigns will work even with empty fields below. Fill in the form
            only if you want to use your own custom SMTP server.
          </p>
        </div>
      )}

      {/* Form */}
      <div className="border-border bg-background space-y-6 rounded-xl border p-6">
        {/* SMTP Host */}
        <div>
          <label
            htmlFor="smtp-host"
            className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Globe" className="h-4 w-4" />
            SMTP Host
          </label>
          <p className="text-muted-foreground mb-2 text-xs">
            Input the domain of your email service provider (e.g., sendgrid.com)
            to establish a connection for sending emails.
          </p>
          <input
            id="smtp-host"
            type="text"
            value={formData.smtp_host}
            onChange={(e) => handleChange("smtp_host", e.target.value)}
            placeholder="smtp.netcorecloud.net"
            className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
          />
        </div>

        {/* SMTP Username */}
        <div>
          <label
            htmlFor="smtp-username"
            className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="User" className="h-4 w-4" />
            SMTP Username
          </label>
          <p className="text-muted-foreground mb-2 text-xs">
            Provide the username for your SMTP account for authentication.
          </p>
          <input
            id="smtp-username"
            type="text"
            value={formData.smtp_username}
            onChange={(e) => handleChange("smtp_username", e.target.value)}
            placeholder="username"
            className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
          />
        </div>

        {/* SMTP Password */}
        <div>
          <label
            htmlFor="smtp-password"
            className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Lock" className="h-4 w-4" />
            SMTP Password
          </label>
          <p className="text-muted-foreground mb-2 text-xs">
            Input the password for your SMTP account to secure your email
            communications.
          </p>
          <input
            id="smtp-password"
            type="password"
            value={formData.smtp_password}
            onChange={(e) => handleChange("smtp_password", e.target.value)}
            placeholder="••••••••••••••••"
            className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
          />
        </div>

        {/* Encryption & Port Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* SMTP Encryption */}
          <div>
            <label
              htmlFor="smtp-encryption"
              className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="Shield" className="h-4 w-4" />
              SMTP Encryption Settings
            </label>
            <p className="text-muted-foreground mb-2 text-xs">
              Choose the encryption method (e.g., TLS).
            </p>
            <NativeSelect
              id="smtp-encryption"
              value={formData.smtp_encryption}
              onChange={(e) => handleChange("smtp_encryption", e.target.value)}
              className="custom-select border-border bg-background focus:ring-ring text-foreground w-full cursor-pointer rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
            >
              <option value="TLS">TLS</option>
              <option value="SSL">SSL</option>
              <option value="NONE">None</option>
            </NativeSelect>
          </div>

          {/* SMTP Port */}
          <div>
            <label
              htmlFor="smtp-port"
              className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
            >
              <Icon name="Hash" className="h-4 w-4" />
              SMTP Port
            </label>
            <p className="text-muted-foreground mb-2 text-xs">
              Specify the port (usually 587 or 465).
            </p>
            <input
              id="smtp-port"
              type="text"
              value={formData.smtp_port}
              onChange={(e) => handleChange("smtp_port", e.target.value)}
              placeholder="465"
              className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
            />
          </div>
        </div>

        {/* Sender Email Address */}
        <div>
          <label
            htmlFor="smtp-sender-email"
            className="text-primary mb-1 flex items-center gap-2 text-sm font-medium"
          >
            <Icon name="Mail" className="h-4 w-4" />
            Sender Email Address
          </label>
          <p className="text-muted-foreground mb-2 text-xs">
            The email address your customers will see as the sender.
          </p>
          <input
            id="smtp-sender-email"
            type="email"
            value={formData.sender_email}
            onChange={(e) => handleChange("sender_email", e.target.value)}
            placeholder="marketing@yourdomain.com"
            className="border-border bg-background placeholder:text-muted-foreground focus:ring-ring text-foreground w-full rounded-lg border px-4 py-2.5 outline-none focus:border-transparent focus:ring-2"
          />
        </div>

        {/* Actions */}
        <div className="border-border flex items-center justify-end gap-3 border-t pt-4">
          {onCancel && (
            <button
              type="button"
              onClick={handleCancel}
              className="text-muted-foreground hover:text-foreground px-6 py-2 transition-colors"
            >
              Cancel
            </button>
          )}
          <Button
            variant="default"
            type="button"
            onClick={handleSave}
            disabled={saveConfigMutation.isPending}
            aria-busy={saveConfigMutation.isPending}
            className="flex items-center gap-2 px-6 py-2"
          >
            {saveConfigMutation.isPending && (
              <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-white"></div>
            )}
            <Icon name="Save" className="h-4 w-4" />
            Save Configuration
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EmailConfigurationForm;
